import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spinner } from '@chakra-ui/react';
import { Check, ChevronLeft, Droplet, Edit2, Trash2, X } from 'lucide-react';
import { useNotes } from '../hooks/useNotes';
import { useCollections } from '../hooks/useCollections';
import { NoteCard } from '../components/NoteCard';
import { NotebookLabel } from '../components/CollectionCard';
import { CoverPicker } from '../components/CoverPicker';
import { Doodle, EnterOnce } from '../components/paper';
import { toaster } from '../components/ui';
import { coverFor, isDark } from '../lib/covers';

const PAGE = 'mx-auto max-w-[60rem] px-5 pb-16 pt-[calc(1rem+var(--mobile-nav-offset))] md:px-10 md:pt-10';

/** Round icon chip that reads on any cover: paper-tinted on dark, ink-tinted on light. */
const CoverChip: React.FC<{
  icon: React.ComponentType<{ size?: number }>;
  label: string;
  dark: boolean;
  onClick: () => void;
  primary?: boolean;
  disabled?: boolean;
  active?: boolean;
}> = ({ icon: Icon, label, dark, onClick, primary, disabled, active }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    aria-pressed={active}
    className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border-none transition-[transform,opacity] duration-150 active:scale-90 disabled:cursor-default disabled:opacity-40"
    style={
      primary
        ? { background: 'var(--primary)', color: 'var(--on-primary)' }
        : {
            background: dark ? `rgba(255,255,255,${active ? 0.3 : 0.16})` : `rgba(0,0,0,${active ? 0.16 : 0.08})`,
            color: dark ? '#ffffff' : 'var(--on-surface)',
          }
    }
  >
    <Icon size={label === 'Back' ? 22 : 16} />
  </button>
);

/**
 * One notebook, opened — the web twin of mobile's collection screen
 * (mobile/app/(app)/(tabs)/library/[collectionId].tsx): a band in the cover
 * colour with its spine and paper label (rename in place), then its pages.
 */
export const Collection: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const navigate = useNavigate();
  const { notes, isLoading: notesLoading } = useNotes();
  const { collections, isLoading: collectionsLoading, recolorCollection, updateCollection, deleteCollection } =
    useCollections();
  // Live cover preview while picking; cleared once the server-side colour catches up.
  const [preview, setPreview] = useState<string | null>(null);
  const [colorOpen, setColorOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState('');

  if (notesLoading || collectionsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Spinner size="lg" color="var(--accent)" />
      </div>
    );
  }

  const shelfIndex = collections.findIndex((c) => String(c.id) === collectionId);
  const collection = collections[shelfIndex];

  if (!collection) {
    return (
      <div className={`${PAGE} flex flex-col items-center gap-3 pt-24 text-center`}>
        <Doodle kind="search" size={0.8} />
        <p className="m-0 font-body text-sm text-on-surface-variant">Notebook not found.</p>
        <button type="button" className="pb-btn pb-btn--ghost" onClick={() => navigate('/library')}>
          Back to the shelf
        </button>
      </div>
    );
  }

  const items = notes.filter((n) => collection.note_ids.includes(n.id));
  const cover = preview ?? coverFor(collection, shelfIndex);
  const dark = isDark(cover);

  const commitColor = async (color: string): Promise<void> => {
    try {
      await recolorCollection({ id: collection.id, color });
    } catch {
      toaster.error({ title: 'Could not change the cover.' });
    } finally {
      setPreview(null);
    }
  };

  const startRename = (): void => {
    setName(collection.name);
    setRenaming(true);
  };

  const saveRename = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed === collection.name) {
      setRenaming(false);
      return;
    }
    try {
      await updateCollection({ id: collection.id, payload: { name: trimmed } });
      setRenaming(false);
      toaster.success({ title: 'Renamed.' });
    } catch (err: any) {
      toaster.error({ title: err?.response?.status === 409 ? 'Name already in use.' : 'Rename failed.' });
    }
  };

  const confirmDelete = async (): Promise<void> => {
    if (!window.confirm('Delete notebook? Its pages are kept as loose pages.')) return;
    await deleteCollection(collection.id);
    toaster.info({ title: 'Notebook deleted.' });
    navigate('/library');
  };

  return (
    <div className={PAGE}>
      {/* The opened notebook: a band in the cover colour with its paper label. */}
      <div
        className="relative mb-6 overflow-hidden rounded-[1.75rem] pb-5 pl-6 pr-4 pt-4 transition-colors duration-300 md:pb-7 md:pl-10 md:pr-6 md:pt-5"
        style={{ backgroundColor: cover }}
      >
        <span
          className="absolute bottom-0 left-0 top-0 w-[10.5px] md:w-4"
          style={{ backgroundColor: dark ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.13)' }}
        />
        <div className="relative flex items-center justify-between">
          <CoverChip icon={ChevronLeft} label="Back" dark={dark} onClick={() => navigate('/library')} />
          {renaming ? (
            <div className="flex gap-2">
              <CoverChip icon={X} label="Cancel rename" dark={dark} onClick={() => setRenaming(false)} />
              <CoverChip icon={Check} label="Save name" dark={dark} primary disabled={!name.trim()} onClick={() => void saveRename()} />
            </div>
          ) : (
            <div className="flex gap-2">
              <CoverChip
                icon={Droplet}
                label="Change cover colour"
                dark={dark}
                active={colorOpen}
                onClick={() => setColorOpen((o) => !o)}
              />
              <CoverChip icon={Edit2} label="Rename notebook" dark={dark} onClick={startRename} />
              <CoverChip icon={Trash2} label="Delete notebook" dark={dark} onClick={() => void confirmDelete()} />
            </div>
          )}
        </div>

        {colorOpen && !renaming && (
          <div className="pb-rise relative mt-4 inline-block rounded-2xl bg-surface-container-lowest p-3 shadow-[0_4px_12px_rgba(28,27,25,0.14)]">
            <p className="pb-eyebrow m-0 mb-2">Cover</p>
            <CoverPicker value={cover} onPreview={setPreview} onCommit={(c) => void commitColor(c)} />
          </div>
        )}

        <div className="relative mt-8 max-w-[28rem]">
          <NotebookLabel
            name={collection.name}
            count={collection.note_count}
            size="lg"
            onClickName={startRename}
            editor={
              renaming
                ? { value: name, onChange: setName, onSubmit: () => void saveRename(), onCancel: () => setRenaming(false) }
                : undefined
            }
          />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center gap-3 pt-2 text-center">
          <Doodle kind="notebooks" size={0.8} />
          <p className="m-0 px-4 font-body text-sm text-on-surface-variant">
            This notebook is waiting for pages. Drag loose pages onto it from your shelf.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {items.map((note, i) => (
            <EnterOnce key={note.id} id={`note:${note.id}`} index={i}>
              <NoteCard note={note} />
            </EnterOnce>
          ))}
        </div>
      )}
    </div>
  );
};
